// Shared date and duration formatting utilities
// Replaces duplicate formatDate/formatDuration across components

/**
 * Format date as short string: "23. jan 2026"
 * Used in lists and cards (StudyArchive, TopicNotes, SessionNoteCard)
 */
export function formatDateShort(date: Date | string | undefined): string {
  if (!date) return 'Ukendt dato';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return 'Ukendt dato';
  return d.toLocaleDateString('da-DK', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * Format date with time: "23. januar 2026, 14:30"
 * Used in detail views (SessionSummary, LibraryPage)
 */
export function formatDateWithTime(date: Date | string | undefined): string {
  if (!date) return 'Ukendt dato';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return 'Ukendt dato';
  return d.toLocaleDateString('da-DK', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format session date: "23. jan 2026, kl. 14:30"
 * Used specifically in SessionHistory for compact display
 */
export function formatSessionDate(dateValue: Date | string | undefined): string {
  if (!dateValue) return 'Ukendt dato';
  try {
    const date = typeof dateValue === 'string' ? new Date(dateValue) : dateValue;
    if (isNaN(date.getTime())) return 'Ukendt dato';
    const day = date.getDate();
    const monthNames = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}. ${month} ${year}, kl. ${hours}:${minutes}`;
  } catch {
    return 'Ukendt dato';
  }
}

/**
 * Format date as long string: "23. januar 2026"
 * Used in TopicNotesView (long month, no time)
 */
export function formatDateLong(date: Date | string | undefined): string {
  if (!date) return 'Ukendt dato';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return 'Ukendt dato';
  return d.toLocaleDateString('da-DK', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/**
 * Format date compact with time: "23. jan, 14:30"
 * Used in SessionNoteCard (short month + time, no year)
 */
export function formatDateCompact(date: Date | string | undefined): string {
  if (!date) return 'Ukendt dato';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return 'Ukendt dato';
  return d.toLocaleDateString('da-DK', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format duration in minutes to human-readable string
 * Examples: "12 min", "1t 30min", "2 timer"
 */
export function formatDuration(minutes: number | undefined): string {
  if (!minutes || minutes <= 0) return 'Ukendt varighed';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remaining = minutes % 60;
  if (remaining === 0) return `${hours} time${hours > 1 ? 'r' : ''}`;
  return `${hours}t ${remaining}min`;
}
